import { useEffect, useRef } from "react";

import MultiDnDWorker from "../worker/multiDnDWorker";
import WorkerBuilder from "../worker/workerBuilder";

import type {
  MultiDnDResult,
  MultiDnDWorkerMessage,
} from "../worker/multiDnDWorker";
import type { MultiDnDWorkerInstance } from "../worker/workerBuilder";

export interface UseMultiDnDWorkerParams {
  onMessage: (message: MessageEvent<MultiDnDResult>) => void;
  onError: () => void;
}

export interface UseMultiDnDWorkerResult {
  isWorkerAvailable: () => boolean;
  postMultiDnDMessage: (message: MultiDnDWorkerMessage) => void;
}

// Worker created/destroyed in one effect (not during render): StrictMode's
// mount->unmount->mount then tears down and recreates a live worker instead
// of terminating the one lazy-ref'd during render and reusing it dead.
export function useMultiDnDWorker({
  onMessage,
  onError,
}: UseMultiDnDWorkerParams): UseMultiDnDWorkerResult {
  const workerRef = useRef<MultiDnDWorkerInstance | null>(null);
  const handlersRef = useRef<UseMultiDnDWorkerParams>({ onMessage, onError });
  handlersRef.current = { onMessage, onError };

  useEffect(() => {
    const worker = new WorkerBuilder(MultiDnDWorker);

    worker.onmessage = (message) => handlersRef.current.onMessage(message);
    worker.onerror = () => handlersRef.current.onError();

    workerRef.current = worker;

    return () => {
      worker.terminate();
      URL.revokeObjectURL(worker.blobUrl);
      workerRef.current = null;
    };
  }, []);

  const isWorkerAvailable = (): boolean => workerRef.current !== null;

  const postMultiDnDMessage = (message: MultiDnDWorkerMessage): void => {
    workerRef.current?.postMessage(message);
  };

  return { isWorkerAvailable, postMultiDnDMessage };
}
